import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  ArrowRight,
  CheckCircle,
  Settings,
  ClipboardList,
  CalendarClock,
  Workflow,
  BarChart3,
  FileText,
  Building2,
  Heart,
  Users,
  TrendingUp,
} from "lucide-react";
import SEO from "../components/SEO";
import StructuredData from "../components/StructuredData";
import MidPageCTA from "../components/MidPageCTA";
import SectionDivider from "../components/SectionDivider";

const ClinicOperations = () => {
  const outcomes = [
    "Streamlined daily workflows that reduce bottlenecks and wasted time",
    "Clear SOPs your team can follow without constant supervision",
    "Optimized scheduling that maximizes provider utilization",
    "Smoother patient intake and front desk processes",
    "Reduced errors from miscommunication or unclear handoffs",
    "Less time spent firefighting and more time on strategic priorities",
    "Operational systems that scale as your practice grows",
  ];

  const whoIsFor = [
    { icon: Building2, label: "Clinics where things keep falling through the cracks" },
    { icon: Heart, label: "Practices struggling with scheduling chaos or no-shows" },
    { icon: Users, label: "Multi-provider groups with inconsistent workflows" },
    { icon: TrendingUp, label: "Growing practices that have outgrown their current systems" },
  ];

  return (
    <div>
      <SEO
        title="Clinic Operations Consulting - Streamline Your Healthcare Practice"
        description="If your practice feels like organized chaos, we can help. RNB Consulting Group works with clinics to fix broken workflows, optimize scheduling, and build operational systems that actually work."
        keywords="clinic operations consulting, healthcare workflow optimization, medical practice scheduling, clinic efficiency improvement, healthcare operations management, streamline medical practice, practice management consulting"
      />
      <StructuredData data={[{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.rnbconsultinggroup.com" },
          { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://www.rnbconsultinggroup.com/services" },
          { "@type": "ListItem", "position": 3, "name": "Clinic Operations", "item": "https://www.rnbconsultinggroup.com/services/clinic-operations" }
        ]
      }]} />

      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 bg-gradient-to-br from-rnb-navy via-rnb-navy to-rnb-blue overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-20 w-72 h-72 bg-rnb-teal/15 rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-10 w-96 h-96 bg-rnb-blue/20 rounded-full blur-3xl" />
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div className="text-white">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-8">
                <Settings className="w-4 h-4 text-rnb-teal" />
                <span className="text-sm font-medium">Clinic Operations Consulting</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6" data-testid="clinic-ops-hero-headline">
                Stop Managing Chaos
                <span className="block mt-2 bg-gradient-to-r from-rnb-teal to-cyan-300 bg-clip-text text-transparent">
                  Start Running a System
                </span>
              </h1>

              <p className="text-lg sm:text-xl text-white/80 leading-relaxed mb-8">
                When your clinic runs on workarounds, memory, and "we've always done it this way". things break. We help you replace the chaos with clear, repeatable systems your team can actually follow.
              </p>

              <ul className="flex flex-wrap gap-x-6 gap-y-3 mb-10 text-white/90 text-sm sm:text-base">
                <li className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-rnb-teal flex-shrink-0" /> Eliminate bottlenecks</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-rnb-teal flex-shrink-0" /> Optimize scheduling</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-rnb-teal flex-shrink-0" /> Build real SOPs</li>
              </ul>

              <Link to="/consultation">
                <Button
                  size="lg"
                  className="bg-rnb-teal hover:bg-rnb-teal/90 text-white rounded-full px-8 py-6 text-lg font-semibold shadow-glow hover:shadow-glow-lg transition-all duration-300 hover:-translate-y-1"
                  data-testid="clinic-ops-hero-cta"
                >
                  Schedule a Free Consultation
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>

            <div className="relative">
              <img
                src="/images/healthcare/staff-dual-monitors.jpg"
                alt="Healthcare staff managing clinic operations at dual monitor workstation"
                className="rounded-2xl shadow-2xl w-full h-56 sm:h-72 lg:h-auto object-cover border-2 border-white/10"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Problem Acknowledgment */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-rnb-navy mb-4">
              Sound Familiar?
            </h2>
            <p className="text-rnb-gray text-lg max-w-2xl mx-auto">
              These are the operational headaches we hear about every single week from practice owners and clinic managers.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            {[
              "Your schedule is either overbooked or full of gaps. there's no middle ground",
              "Staff members do the same task differently because there are no written processes",
              "Patient intake takes too long and your front desk is always behind",
              "You keep finding mistakes that should have been caught earlier in the workflow",
              "You're the only person who knows how everything is supposed to work",
              "Growing feels impossible because your current systems can barely handle today's volume",
            ].map((problem, idx) => (
              <div key={problem.slice(0, 40)} className="flex items-start gap-3 p-4 rounded-xl bg-slate-50 border border-slate-100">
                <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-orange-500 text-xs font-bold">{idx + 1}</span>
                </div>
                <p className="text-rnb-gray leading-relaxed">{problem}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How We Help */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-rnb-navy mb-4">
              How We Fix What's Not Working
            </h2>
            <p className="text-rnb-gray text-lg max-w-2xl mx-auto">
              We get into the details of how your practice actually runs day-to-day. and we build systems that make it better.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: Workflow, title: "Workflow Mapping & Redesign", desc: "We map your current processes end-to-end, identify where things break down, and redesign workflows that are logical, efficient, and easy for your team to follow." },
              { icon: CalendarClock, title: "Scheduling Optimization", desc: "We analyze your scheduling patterns, no-show rates, and provider utilization to build templates that keep your day flowing smoothly without double-bookings or dead time." },
              { icon: ClipboardList, title: "SOP Development", desc: "We create clear, practical standard operating procedures for your most critical processes. from patient intake to end-of-day close-out. so every team member knows exactly what to do." },
              { icon: Settings, title: "Front Desk & Intake Redesign", desc: "Your front desk sets the tone. We streamline check-in/check-out, paperwork flow, insurance verification, and communication so the experience is faster and more professional." },
              { icon: BarChart3, title: "Performance Metrics & Dashboards", desc: "You can't improve what you don't measure. We help you set up the right KPIs and reporting tools so you can see how your operations are performing at a glance." },
              { icon: FileText, title: "Process Documentation & Training", desc: "We don't just document processes. we train your team on them. Everyone leaves knowing what's expected, how to do it, and where to find the answers when they're unsure." },
            ].map((item, idx) => (
              <Card key={item.title || item.name || item.label || idx} className="border-0 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-7">
                  <div className="w-12 h-12 bg-rnb-light rounded-xl flex items-center justify-center mb-5">
                    <item.icon className="w-6 h-6 text-rnb-blue" />
                  </div>
                  <h3 className="text-lg font-bold text-rnb-navy mb-2">{item.title}</h3>
                  <p className="text-rnb-gray text-sm leading-relaxed">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Mid-page CTA */}
      <MidPageCTA variant={0} image="/images/healthcare/clinic-reception.jpg" />

      {/* Outcomes */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-rnb-navy mb-4">
              What You Can Expect
            </h2>
            <p className="text-rnb-gray text-lg max-w-2xl mx-auto">
              Practices we work with stop running on adrenaline and start running on systems. and the difference shows up everywhere.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {outcomes.map((outcome, idx) => (
              <div key={outcome.slice(0, 40)} className="flex items-start gap-3 p-4">
                <CheckCircle className="w-5 h-5 text-rnb-teal flex-shrink-0 mt-0.5" />
                <span className="text-rnb-navy font-medium">{outcome}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-rnb-navy mb-4">
              Who This Is For
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            {whoIsFor.map((item, idx) => (
              <div key={item.title || item.name || item.label || idx} className="flex items-center gap-4 p-5 bg-white rounded-xl shadow-sm">
                <div className="w-11 h-11 bg-rnb-light rounded-xl flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-rnb-blue" />
                </div>
                <span className="text-rnb-navy font-medium">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why RNB */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-rnb-navy mb-4">
              Why Healthcare Practices Choose RNB
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            {[
              { icon: Heart, title: "Healthcare-Only Focus", desc: "We don't do generic business consulting. We understand the unique pace, regulations, and realities of running a clinical practice. and we build solutions around that." },
              { icon: ClipboardList, title: "Hands-On Implementation", desc: "We don't hand you a binder of recommendations. We work alongside your team to implement changes, train staff, and make sure new systems actually get adopted." },
              { icon: BarChart3, title: "Measurable Results", desc: "We track the impact of every change. from scheduling efficiency to patient throughput. so you can see exactly what's improving and where to focus next." },
              { icon: Users, title: "Team-Centered Approach", desc: "Operations only work if your team buys in. We involve staff in the process, get their input, and make sure the systems we build work for the people using them every day." },
            ].map((item, idx) => (
              <div key={item.title || item.name || item.label || idx} className="flex items-start gap-4 p-5">
                <div className="w-10 h-10 bg-rnb-teal/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-rnb-teal" />
                </div>
                <div>
                  <h3 className="font-bold text-rnb-navy mb-1">{item.title}</h3>
                  <p className="text-rnb-gray text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-rnb-navy to-rnb-blue relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-rnb-teal/15 rounded-full blur-3xl" />
        </div>

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Your Practice Shouldn't Run on Workarounds
          </h2>
          <p className="text-lg text-white/80 mb-10 max-w-2xl mx-auto">
            Every day you operate without clear systems is a day of lost efficiency, preventable mistakes, and unnecessary stress. Let's fix the foundation so everything else gets easier.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/consultation">
              <Button
                size="lg"
                className="bg-white text-rnb-navy hover:bg-white/90 rounded-full px-8 py-6 text-lg font-semibold shadow-lg hover:-translate-y-1 transition-all duration-300"
                data-testid="clinic-ops-bottom-cta"
              >
                Book a Free Consultation
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/quote">
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-semibold transition-all duration-300"
              >
                Get an Instant Quote
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ClinicOperations;
